import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { Modal } from "@components/ui/Modal";
import { useGetWellSurveysQuery, useGetWellEventsQuery } from "@api/pApi";
import { fmtDepth, fmtNum } from "@lib/format";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";
import type { MessageKey } from "@i18n/messages";
import {
  useCreateDirectionalStationMutation,
  useUpdateDirectionalStationMutation,
  type DailySurveyStationOut,
  type DirectionalStationCreate,
  type DirectionalStationUpdate,
} from "./directionalApi";

/**
 * Directional input — the EDITABLE day panel for daily directional stations.
 *
 * daily_report-scoped and DRILLING-ONLY: Oxbo-P returns 422 for a non-drilling
 * event, so when the active event isn't a drilling job the ADD/EDIT controls
 * are present but disabled with a "solo en eventos de perforación" note.
 *
 * Lists the day's DAILY stations (`source === "daily"`, scoped to this event)
 * from the well-scoped read GET /wells/{uwi}/surveys, and lets the user EDIT
 * (PUT /reports/{report_id}/directional/{entity_id}) or ADD (POST
 * /reports/{report_id}/directional). `md` is required on ADD; TVD/DLS are NOT
 * entered (computed downstream). On success the well surveys list is refetched
 * (see directionalApi) so the Perfilajes screen reflects the new station.
 */

/** The read query returns the base station shape; the daily rows carry the
 * write-contract fields (entity_id / source). We read them via this superset
 * without editing the shared read type. */
type ReadStation = DailySurveyStationOut;

/** Parse a numeric text input to number | null (blank → null). */
function parseNum(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Pull the HTTP status off an RTK Query error, if any. */
function errStatus(e: FetchBaseQueryError | SerializedError | undefined): number | undefined {
  if (e && "status" in e && typeof e.status === "number") return e.status;
  return undefined;
}

/** Map a mutation error to a house message key (403 / 422 / generic). */
function errKey(e: FetchBaseQueryError | SerializedError | undefined): MessageKey {
  const s = errStatus(e);
  if (s === 403) return "dir.err.forbidden";
  if (s === 422) return "dir.err.notDrilling";
  return "dir.err.generic";
}

interface RowDraft {
  md: string;
  inclination: string;
  azimuth: string;
  sequence_no: string;
  survey_date: string;
}

const EMPTY_DRAFT: RowDraft = {
  md: "",
  inclination: "",
  azimuth: "",
  sequence_no: "",
  survey_date: "",
};

function draftFromStation(s: ReadStation): RowDraft {
  return {
    md: s.md != null ? String(s.md) : "",
    inclination: s.inclination != null ? String(s.inclination) : "",
    azimuth: s.azimuth != null ? String(s.azimuth) : "",
    sequence_no: s.sequence_no != null ? String(s.sequence_no) : "",
    survey_date: s.survey_date ?? "",
  };
}

export function DirectionalEditPanel({
  reportId,
  eventId,
  wellUwi,
}: {
  reportId: string;
  eventId: string;
  wellUwi: string;
}) {
  const { t, locale } = useLocale();
  const {
    data: surveys,
    isLoading,
    isError,
    refetch,
  } = useGetWellSurveysQuery(wellUwi, { skip: !wellUwi });

  // Resolve the active event's type to gate the DRILLING-ONLY write layer.
  const { data: events = [] } = useGetWellEventsQuery(wellUwi, { skip: !wellUwi });
  const isDrilling = useMemo(
    () => events.find((e) => e.id === eventId)?.event_type === "drilling",
    [events, eventId],
  );

  const [createStation, createState] = useCreateDirectionalStationMutation();
  const [updateStation, updateState] = useUpdateDirectionalStationMutation();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<RowDraft>(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<RowDraft>(EMPTY_DRAFT);
  const [addError, setAddError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Flatten every station the read serves (passes + ungrouped) and keep the
  // DAILY ones for THIS event — the hand-entered stations this panel owns.
  const dailyStations = useMemo<ReadStation[]>(() => {
    if (!surveys) return [];
    const all: ReadStation[] = [
      ...surveys.passes.flatMap((p) => p.stations as unknown as ReadStation[]),
      ...(surveys.ungrouped as unknown as ReadStation[]),
    ];
    return all
      .filter((s) => s.source === "daily" && (!eventId || s.event_id === eventId))
      .sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0) || (a.md ?? 0) - (b.md ?? 0));
  }, [surveys, eventId]);

  const canWrite = isDrilling;
  const busy = createState.isLoading || updateState.isLoading;

  const startEdit = (s: ReadStation) => {
    setEditingId(s.entity_id);
    setEditDraft(draftFromStation(s));
    setEditError(null);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const saveEdit = async (entityId: string) => {
    setEditError(null);
    const body: DirectionalStationUpdate = {
      md: parseNum(editDraft.md) ?? undefined,
      inclination: parseNum(editDraft.inclination),
      azimuth: parseNum(editDraft.azimuth),
      sequence_no: parseNum(editDraft.sequence_no),
      survey_date: editDraft.survey_date.trim() || null,
    };
    try {
      await updateStation({ reportId, entityId, wellUwi, body }).unwrap();
      setEditingId(null);
    } catch (e) {
      setEditError(t(errKey(e as FetchBaseQueryError)));
    }
  };

  const submitAdd = async () => {
    setAddError(null);
    const md = parseNum(addDraft.md);
    if (md == null) {
      setAddError(t("dir.err.mdRequired"));
      return;
    }
    const body: DirectionalStationCreate = {
      md,
      inclination: parseNum(addDraft.inclination),
      azimuth: parseNum(addDraft.azimuth),
      sequence_no: parseNum(addDraft.sequence_no),
      survey_date: addDraft.survey_date.trim() || null,
    };
    try {
      await createStation({ reportId, wellUwi, body }).unwrap();
      setAdding(false);
      setAddDraft(EMPTY_DRAFT);
    } catch (e) {
      setAddError(t(errKey(e as FetchBaseQueryError)));
    }
  };

  return (
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("dir.subtitle")}</p>
      </div>

      {!canWrite ? (
        <div className="empty-state" style={{ marginBottom: 12 }}>
          {t("dir.onlyDrilling")}
        </div>
      ) : null}

      <div className="card">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <h3>{t("dir.stations")}</h3>
          <button
            type="button"
            className="btn primary"
            disabled={!canWrite || busy || adding}
            onClick={() => {
              setAdding(true);
              setAddDraft(EMPTY_DRAFT);
              setAddError(null);
            }}
            title={!canWrite ? t("dir.onlyDrilling") : undefined}
          >
            {t("dir.add")}
          </button>
        </div>

        <div className="table-scroll" style={{ marginTop: 10 }}>
          <table>
            <thead>
              <tr>
                <th className="num">{t("dir.col.seq")}</th>
                <th className="num">{t("dir.col.md")}</th>
                <th className="num">{t("dir.col.inc")}</th>
                <th className="num">{t("dir.col.azi")}</th>
                <th className="num">{t("dir.col.tvd")}</th>
                <th className="num">{t("dir.col.dls")}</th>
                <th>{t("dir.col.date")}</th>
                <th>{t("dir.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {dailyStations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    {t("dir.empty")}
                  </td>
                </tr>
              ) : null}

              {dailyStations.map((s) => (
                <tr key={s.entity_id} data-owner="z">
                  <td className="num tab-nums">{s.sequence_no ?? "—"}</td>
                  <td className="num tab-nums">{fmtDepth(s.md, locale)}</td>
                  <td className="num tab-nums">
                    {fmtNum(s.inclination, locale, { maximumFractionDigits: 2 })}
                  </td>
                  <td className="num tab-nums">
                    {fmtNum(s.azimuth, locale, { maximumFractionDigits: 2 })}
                  </td>
                  <td className="num tab-nums">{fmtNum(s.tvd, locale, { maximumFractionDigits: 1 })}</td>
                  <td className="num tab-nums">
                    {fmtNum(s.dogleg_severity, locale, { maximumFractionDigits: 2 })}
                  </td>
                  <td>{s.survey_date ?? "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn"
                      disabled={!canWrite || busy || adding}
                      onClick={() => startEdit(s)}
                      title={!canWrite ? t("dir.onlyDrilling") : undefined}
                    >
                      {t("dir.edit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={adding || editingId != null}
        title={adding ? t("dir.add") : t("dir.edit")}
        busy={busy}
        confirmLabel={busy ? t("dir.saving") : t("dir.save")}
        cancelLabel={t("dir.cancel")}
        footerNote={adding ? addError : editError}
        onClose={() => {
          if (adding) {
            setAdding(false);
            setAddError(null);
          } else {
            cancelEdit();
          }
        }}
        onConfirm={() => {
          if (adding) {
            void submitAdd();
          } else if (editingId != null) {
            void saveEdit(editingId);
          }
        }}
      >
        <div className="modal-grid">
          <label>
            <span>{t("dir.col.seq")}</span>
            <input
              inputMode="numeric"
              value={adding ? addDraft.sequence_no : editDraft.sequence_no}
              onChange={(e) =>
                adding
                  ? setAddDraft({ ...addDraft, sequence_no: e.target.value })
                  : setEditDraft({ ...editDraft, sequence_no: e.target.value })
              }
              aria-label={t("dir.col.seq")}
            />
          </label>
          <label>
            <span>{t("dir.col.md")}</span>
            <input
              inputMode="decimal"
              value={adding ? addDraft.md : editDraft.md}
              onChange={(e) =>
                adding
                  ? setAddDraft({ ...addDraft, md: e.target.value })
                  : setEditDraft({ ...editDraft, md: e.target.value })
              }
              aria-label={t("dir.col.md")}
              placeholder={adding ? t("dir.md.required") : undefined}
            />
          </label>
          <label>
            <span>{t("dir.col.inc")}</span>
            <input
              inputMode="decimal"
              value={adding ? addDraft.inclination : editDraft.inclination}
              onChange={(e) =>
                adding
                  ? setAddDraft({ ...addDraft, inclination: e.target.value })
                  : setEditDraft({ ...editDraft, inclination: e.target.value })
              }
              aria-label={t("dir.col.inc")}
            />
          </label>
          <label>
            <span>{t("dir.col.azi")}</span>
            <input
              inputMode="decimal"
              value={adding ? addDraft.azimuth : editDraft.azimuth}
              onChange={(e) =>
                adding
                  ? setAddDraft({ ...addDraft, azimuth: e.target.value })
                  : setEditDraft({ ...editDraft, azimuth: e.target.value })
              }
              aria-label={t("dir.col.azi")}
            />
          </label>
          <label>
            <span>{t("dir.col.date")}</span>
            <input
              type="date"
              value={adding ? addDraft.survey_date : editDraft.survey_date}
              onChange={(e) =>
                adding
                  ? setAddDraft({ ...addDraft, survey_date: e.target.value })
                  : setEditDraft({ ...editDraft, survey_date: e.target.value })
              }
              aria-label={t("dir.col.date")}
            />
          </label>
        </div>
      </Modal>
    </QueryState>
  );
}
